// Frontend client for the AI pipeline running on /api/ai.
//
// The three-agent orchestration (Intelligence → Quality → Publishing)
// lives on the backend so API keys never touch the browser. This module
// is deliberately thin: no state, no caching, just the HTTP contract.

const BASE = process.env.REACT_APP_BACKEND_URL;

/** Explicit pipeline states mirrored from `backend/ai/config.py`. */
export const PIPELINE_STATUS = Object.freeze({
  QUEUED:           "queued",
  ANALYZING:        "analyzing",
  NOT_OPPORTUNITY:  "not_opportunity",
  QUALITY_REVIEW:   "quality_review",
  QUALITY_FAILED:   "quality_failed",
  PREPARING:        "preparing",
  PENDING_REVIEW:   "pending_review",
  PUBLISHED:        "published",
  ARCHIVED:         "archived",
  FAILED:           "failed",
});

/** Client-side "stage" labels used by the UI progress indicator. */
export const PIPELINE_STAGES = [
  { key: PIPELINE_STATUS.ANALYZING,      label: "وكيل الاستخبارات",       hint: "استخلاص الفرصة من المقال" },
  { key: PIPELINE_STATUS.QUALITY_REVIEW, label: "وكيل الجودة",             hint: "التحقّق من الأدلّة والاتساق" },
  { key: PIPELINE_STATUS.PREPARING,      label: "وكيل تجهيز النشر",        hint: "بناء بطاقة العرض" },
];

const asAnalyzePayload = (item) => ({
  news_id:      item.id,
  headline:     item.headline,
  full_text:    item.fullText,
  source_name:  item.source,
  source_url:   item.sourceUrl ?? null,
  published_at: item.publishedAtIso ?? item.publishedAt,
  country:      item.country ?? null,
});

const request = async (path, { method = "POST", body } = {}) => {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const err = await res.json();
      detail = err?.detail || detail;
    } catch { /* ignore */ }
    throw new Error(`(${res.status}) ${detail}`);
  }
  return res.json();
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Start the pipeline (background job on the backend) and poll for the
 * final result. Ingress caps blocking requests at ~60 s so we can't wait
 * synchronously — this pattern is required, not optional.
 *
 * `onStage(stageIndex)` is invoked as we advance client-side stage hints
 * during the wait. It is decoupled from the backend job state because the
 * backend runs the three agents inline; we approximate progression using
 * elapsed time (with obvious padding).
 */
const runJob = async (path, item, onStage) => {
  const start = await request(path, { body: asAnalyzePayload(item) });
  if (!start?.job_id) throw new Error("لم يستلم الخادم مهمة تحليل صالحة");

  // Client-side stage hints — 3 agents, ~35s each on average.
  const stageTimers = [
    setTimeout(() => onStage?.(1), 30000),   // move to Quality
    setTimeout(() => onStage?.(2), 60000),   // move to Publishing
  ];

  try {
    // Poll — 2 s cadence, 5 min ceiling. Fires roughly every 2s while
    // the pipeline runs; typical run completes in 60-130 s.
    const deadline = Date.now() + 300000;
    while (Date.now() < deadline) {
      await sleep(2000);
      const status = await request(`/api/ai/analyze/${start.job_id}`, { method: "GET" });
      if (status.state === "done")  return status.result;
      if (status.state === "error") throw new Error(status.error || "خطأ في التحليل");
    }
    throw new Error("انتهت مهلة التحليل (5 دقائق)");
  } finally {
    stageTimers.forEach(clearTimeout);
  }
};

export const analyzeNews   = (item, onStage) => runJob("/api/ai/analyze",   item, onStage);
export const reanalyzeNews = (item, onStage) => runJob("/api/ai/reanalyze", item, onStage);
export const archiveNews   = (id)            => request(`/api/ai/archive/${id}`);
export const sendToReview  = (id)            => request(`/api/ai/send-to-review/${id}`);
